import { useMemo } from "react";

import { useCurrentTill } from "~/api/tills/hooks/use-till-current";

type UseVatLabelReturn = Record<VatLabels, string>;

const DEFAULT_VAT_LABELS: Record<VatLabels, string> = {
  vatLabel: "VAT",
  vatNumberLabel: "VAT Number"
};

/**
 * Tills responses from @kanpla/api-contract >= 1.52.0 carry server-resolved
 * tax wording; the pinned contract predates the field, so it is read through
 * this shape until the package is bumped.
 */
type WithTaxLabels = {
  taxLabels?: Record<"vat" | "vat_number", string>;
};

/**
 * Tax wording resolved by the backend from the partner's country and the
 * till's language (single source of truth: `getTaxLabelsByCountry` in the
 * kanpla monorepo). Falls back to English wording until the till has loaded
 * or when the API predates the `taxLabels` field.
 */
export const useVatLabel = (): UseVatLabelReturn => {
  const { data: taxLabels } = useCurrentTill(
    (till) => (till as WithTaxLabels).taxLabels
  );

  return useMemo(() => {
    if (!taxLabels) {
      return DEFAULT_VAT_LABELS;
    }

    return {
      vatLabel: taxLabels.vat,
      vatNumberLabel: taxLabels.vat_number
    };
  }, [taxLabels]);
};

export type VatLabels = "vatLabel" | "vatNumberLabel";
